import React, { useState, useRef, useMemo } from "react";
import { Briefcase, Plus, LogOut, MapPin, Clock, Loader2, Edit2, Calendar, Menu, X, ChevronRight, ChevronLeft, ChevronDown, CheckCircle, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Modal, StatusBadge, DarkModeToggle, AddressInput } from "./ui";
import { PlanningCalendar } from "./PlanningCalendar";
import { DossierNotes } from "./DossierNotes";
import { SignedQuoteUpload } from "./commercial/SignedQuoteUpload";
import { JobsFiltersBar, useJobsFilters, STATUS_LABELS } from "./JobsFiltersBar";
import { JobsCategoryHub } from "./JobsCategoryHub";
import { JobsDossierTypeHub } from "./JobsDossierTypeHub";
import {
  categoryRequiresDossierType,
  DOSSIER_TYPE_FILTER_LABELS,
  JOB_CATEGORY_LABELS,
  jobMatchesCategory,
} from "../utils/jobCategories";
import type { DossierTypeFilter, JobCategory } from "../utils/jobCategories";
import type { AddressInputHandle } from "./ui/AddressInput";
import type { Job, StockRegion, User } from "../types";
import {
  buildClientNameForDossierType,
  isPacDossierType,
  showDehumidifierTechnicalFields,
  showLedTechnicalFields,
  showTimerTechnicalFields,
  usesRenovationQuestions,
  usesTechnicalQuestions,
} from "../utils/dossierLabels";

interface PartnerAppProps {
  jobs: Job[];
  currentUser: User;
  onAddJob: (jobData: {
    dossierType?: "led" | "led_exterieur" | "led_interieur" | "deshumidificateur" | "vmc_agricoles" | "th_122" | "renovation" | "hp_bp_flottante" | "pac";
    clientName: string;
    clientPhone: string;
    clientAddress: string;
    surface?: string;
    lightingType?: string;
    ceilingHeight?: string;
    estimatedLedCount?: string;
    dehumidifierCount?: number;
    timerCount?: number;
    needsCounter?: boolean;
    needsGenerator?: boolean;
    notes?: string;
    /** Région de stock (maison) pour ce dossier. */
    stockRegionId?: string;
    /** Date / heure de RDV : uniquement si l'admin a activé l'option pour ce partenaire (sinon ignorées côté serveur). */
    date?: string;
    time?: string;
  }) => Promise<{ error: string | null }>;
  onUpdateJob: (job: Job) => void;
  onDeleteJob: (jobId: string) => void | Promise<void>;
  onLogout: () => void;
  partnerName: string;
  /** Régions de stock (maisons) pour le menu déroulant à la création/édition. */
  stockRegions?: StockRegion[];
  /** Nom de la société de l'admin (affiché comme branding pour le partenaire). */
  ownerCompanyName?: string;
  /** Logo / avatar de l'admin. */
  ownerLogoUrl?: string | null;
  /** Si true (réglage admin sur le profil partenaire), le partenaire peut gérer date/heure sur ses dossiers en attente ou programmés. */
  canPartnerManageOwnSchedule?: boolean;
}

export const PartnerApp = ({
  jobs,
  currentUser,
  onAddJob,
  onUpdateJob,
  onDeleteJob,
  onLogout,
  partnerName,
  stockRegions = [],
  ownerCompanyName = "",
  ownerLogoUrl,
  canPartnerManageOwnSchedule = false,
}: PartnerAppProps) => {
  const DOSSIER_TYPE_OPTIONS: Array<{ value: NonNullable<Job["dossierType"]>; label: string }> = [
    { value: "led_exterieur", label: "LED extérieur" },
    { value: "led_interieur", label: "LED intérieur" },
    { value: "deshumidificateur", label: "Déshumidificateur" },
    { value: "vmc_agricoles", label: "VMC Agricoles" },
    { value: "th_122", label: "TH 122" },
    { value: "renovation", label: "Rénovation" },
    { value: "hp_bp_flottante", label: "HP/BP flottante" },
    { value: "pac", label: "PAC" },
  ];
  const [view, setView] = useState<"list" | "planning">("list");
  const [dossiersScreen, setDossiersScreen] = useState<"hub" | "types" | "list">("hub");
  const [activeCategory, setActiveCategory] = useState<JobCategory | null>(null);
  const [activeDossierType, setActiveDossierType] = useState<DossierTypeFilter | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const jobsFilters = useJobsFilters(jobs, {
    storageKey: `partner-jobs-filters:${currentUser.id}`,
  });

  const goToDossiersHub = () => {
    jobsFilters.setField("category", "");
    jobsFilters.setField("status", "");
    jobsFilters.setField("dossierType", "");
    setActiveCategory(null);
    setActiveDossierType(null);
    setDossiersScreen("hub");
  };

  const goToDossierTypes = () => {
    jobsFilters.setField("dossierType", "");
    setActiveDossierType(null);
    setDossiersScreen("types");
  };

  const handleSelectCategory = (category: JobCategory) => {
    jobsFilters.setField("category", category);
    jobsFilters.setField("status", "");
    jobsFilters.setField("dossierType", "");
    jobsFilters.setField("dateFrom", "");
    jobsFilters.setField("dateTo", "");
    setActiveCategory(category);
    setActiveDossierType(null);
    if (categoryRequiresDossierType(category)) {
      setDossiersScreen("types");
    } else {
      setDossiersScreen("list");
    }
  };

  const handleSelectDossierType = (type: DossierTypeFilter) => {
    jobsFilters.setField("dossierType", type);
    setActiveDossierType(type);
    setDossiersScreen("list");
  };

  const toScheduleJobsForTypes = useMemo(
    () => jobs.filter((j) => jobMatchesCategory(j, "to_schedule")),
    [jobs]
  );

  const viewTitle = view === "list" ? "Dossiers" : "Planning";
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [editingJobInitial, setEditingJobInitial] = useState<Job | null>(null);
  /** true = ouvert depuis "Chantiers en cours" → consultation seule, pas d'enregistrement */
  const [editingJobReadOnly, setEditingJobReadOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const addressRef = useRef<AddressInputHandle>(null);
  const scheduleEditableFor = (job: Job) =>
    canPartnerManageOwnSchedule && (job.status === "pending" || job.status === "scheduled");

  const partnerCanDeleteJob = (job: Job) =>
    job.status === "pending" || job.status === "scheduled" || job.status === "cancelled";

  const [newJob, setNewJob] = useState<{
    dossierType?: "led" | "led_exterieur" | "led_interieur" | "deshumidificateur" | "vmc_agricoles" | "th_122" | "renovation" | "hp_bp_flottante" | "pac";
    clientName: string;
    clientFirstName: string;
    clientLastName: string;
    clientPhone: string;
    surface: string;
    lightingType: string;
    ceilingHeight: string;
    estimatedLedCount: string;
    dehumidifierCount: string;
    timerCount: string;
    needsCounter: boolean;
    needsGenerator: boolean;
    notes: string;
    stockRegionId: string;
    date: string;
    time: string;
  }>({
    dossierType: undefined,
    clientName: "",
    clientFirstName: "",
    clientLastName: "",
    clientPhone: "",
    surface: "",
    lightingType: "",
    ceilingHeight: "",
    estimatedLedCount: "",
    dehumidifierCount: "",
    timerCount: "",
    needsCounter: false,
    needsGenerator: false,
    notes: "",
    stockRegionId: "",
    date: "",
    time: "",
  });
  const [isDossierTypeMenuOpen, setIsDossierTypeMenuOpen] = useState(false);
  const selectedDossierType = DOSSIER_TYPE_OPTIONS.find(
    (option) => option.value === newJob.dossierType
  );

  const resetForm = () => {
    setIsDossierTypeMenuOpen(false);
    setNewJob({
      dossierType: undefined,
      clientName: "",
      clientFirstName: "",
      clientLastName: "",
      clientPhone: "",
      surface: "",
      lightingType: "",
      ceilingHeight: "",
      estimatedLedCount: "",
      dehumidifierCount: "",
      timerCount: "",
      needsCounter: false,
      needsGenerator: false,
      notes: "",
      stockRegionId: "",
      date: "",
      time: "",
    });
  };

  const jobsToShow = jobsFilters.filtered;
  const jobsGrouped = jobsFilters.grouped;
  const isGrouped = jobsFilters.state.groupBy === "status";

  /** Chantiers en cours (visibles sous le planning, non modifiables par le partenaire — comme l'admin) */
  const inProgressJobs = useMemo(
    () =>
      jobs
        .filter((j) => j.status === "in_progress")
        .sort((a, b) => {
          const d = (a.date || "").localeCompare(b.date || "");
          if (d !== 0) return d;
          return (a.time || "").localeCompare(b.time || "");
        }),
    [jobs]
  );

  const { t } = useTranslation();

  const renderJobCard = (job: Job) => (
    <li
      key={job.id}
      onClick={() => {
        setEditingJobReadOnly(false);
        setEditingJob(job);
      }}
      className={`rounded-2xl p-5 shadow-sm transition-shadow overflow-hidden relative cursor-pointer active:scale-[0.99] ${
        job.status === "cancelled"
          ? "bg-red-50/80 dark:bg-red-950/30 border-2 border-red-200 dark:border-red-800/60"
          : "bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-white/10 hover:shadow-md"
      }`}
    >
      {job.status === "cancelled" && (
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-red-500 rounded-l-2xl" aria-hidden />
      )}
      <div className={`flex justify-between items-start gap-3 ${job.status === "cancelled" ? "pl-2" : ""}`}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-semibold text-gray-900 dark:text-white text-[17px] tracking-tight truncate">
              {job.clientName}
            </h3>
            <StatusBadge status={job.status} installerId={job.installerId} date={job.date} />
          </div>
          <p className="flex items-center gap-2 text-gray-500 dark:text-slate-400 text-sm mt-0.5 truncate">
            <MapPin size={14} className="shrink-0 text-gray-400" />
            {job.clientAddress}
          </p>
          <p className="flex items-center gap-2 text-gray-400 dark:text-slate-500 text-xs mt-2">
            <Clock size={12} className="shrink-0" />
            {new Date(job.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
          </p>
          {job.status === "completed" && job.partnerInvoiceTotalCents != null && (
            <p className="text-xs font-medium text-green-600 dark:text-green-400 mt-2">
              Facture : {(job.partnerInvoiceTotalCents / 100).toFixed(2)} €
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {partnerCanDeleteJob(job) && (
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                if (!window.confirm(t("partner.deleteJobConfirm"))) return;
                await onDeleteJob(job.id);
              }}
              className="p-2.5 rounded-xl text-gray-400 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
              title={t("partner.deleteJob")}
            >
              <Trash2 size={18} strokeWidth={2} />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditingJobReadOnly(false);
              setEditingJob(job);
            }}
            className="p-2.5 rounded-xl text-gray-400 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
            title="Modifier le dossier"
          >
            <Edit2 size={18} strokeWidth={2} />
          </button>
        </div>
      </div>
    </li>
  );

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newJob.dossierType) {
      alert("Veuillez choisir le type de dossier.");
      return;
    }
    const res = await addressRef.current?.validate();
    if (!res?.ok) return;
    setSubmitting(true);
    try {
      const payload = {
        ...newJob,
        clientName: buildClientNameForDossierType(newJob.dossierType, {
          clientName: newJob.clientName,
          clientFirstName: newJob.clientFirstName,
          clientLastName: newJob.clientLastName,
        }),
        dehumidifierCount:
          newJob.dehumidifierCount.trim() !== "" ? Number(newJob.dehumidifierCount) : undefined,
        timerCount: newJob.timerCount.trim() !== "" ? Number(newJob.timerCount) : undefined,
        clientAddress: res.label,
      };
      if (!payload.stockRegionId) delete (payload as Record<string, unknown>).stockRegionId;
      if (!canPartnerManageOwnSchedule || !newJob.date?.trim()) {
        delete (payload as Record<string, unknown>).date;
        delete (payload as Record<string, unknown>).time;
      }
      delete (payload as Record<string, unknown>).clientFirstName;
      delete (payload as Record<string, unknown>).clientLastName;
      const result = await onAddJob(payload);
      if (result.error) return;
      setIsModalOpen(false);
      resetForm();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingJob) return;
    setSubmitting(true);
    try {
      onUpdateJob(editingJob);
      setEditingJob(null);
    } finally {
      setSubmitting(false);
    }
  };

  const isPartnerNewJobComplete = (() => {
    if (!newJob.dossierType || !newJob.clientPhone.trim()) return false;
    if (usesRenovationQuestions(newJob.dossierType)) {
      if (!newJob.clientFirstName.trim() || !newJob.clientLastName.trim()) return false;
      return true;
    }
    if (!newJob.clientName.trim()) return false;
    if ((newJob.dossierType === "deshumidificateur" || newJob.dossierType === "vmc_agricoles") && !newJob.surface.trim()) return false;
    if (newJob.dossierType === "th_122" && !newJob.surface.trim()) return false;
    return true;
  })();

  const normalizeEditableJob = (job: Job | null) => {
    if (!job) return null;
    const base = {
      id: job.id,
      dossierType: job.dossierType ?? "",
      clientName: job.clientName ?? "",
      clientPhone: job.clientPhone ?? "",
      clientAddress: job.clientAddress ?? "",
      surface: job.surface ?? "",
      lightingType: job.lightingType ?? "",
      ceilingHeight: job.ceilingHeight ?? "",
      estimatedLedCount: job.estimatedLedCount ?? "",
      notes: job.notes ?? "",
      stockRegionId: job.stockRegionId ?? "",
      status: job.status,
    };
    if (scheduleEditableFor(job)) {
      return { ...base, date: job.date ?? "", time: job.time ?? "" };
    }
    return base;
  };

  const isPartnerEditDirty =
    !!editingJob &&
    !!editingJobInitial &&
    JSON.stringify(normalizeEditableJob(editingJob)) !==
      JSON.stringify(normalizeEditableJob(editingJobInitial));

  React.useEffect(() => {
    if (editingJob?.id) setEditingJobInitial(editingJob);
    else setEditingJobInitial(null);
  }, [editingJob?.id]);

  const brandCard = (
    <div className="mx-4 mt-4 mb-2 rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-xl shrink-0">
      <div className="relative aspect-[3/2] min-h-0">
        {ownerLogoUrl ? (
          <img src={ownerLogoUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
            <Briefcase className="w-14 h-14 text-white/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h2 className="text-lg font-semibold text-white tracking-tight drop-shadow-sm truncate" title={ownerCompanyName || undefined}>
            {ownerCompanyName || "Espace Partenaire"}
          </h2>
          <p className="text-xs text-white/80 truncate mt-0.5">Espace Partenaire · {partnerName}</p>
        </div>
      </div>
    </div>
  );

  const navContent = (
    <nav className="p-4 pt-2 space-y-1.5">
      <button
        type="button"
        onClick={() => { setView("list"); goToDossiersHub(); setMenuOpen(false); }}
        className={`w-full flex items-center gap-4 px-5 py-3.5 rounded-2xl text-left font-medium text-[15px] transition-all ${
          view === "list"
            ? "bg-white/15 text-white shadow-inner"
            : "text-indigo-100 hover:bg-white/10 hover:text-white"
        }`}
      >
        <Briefcase size={22} /> Dossiers
      </button>
      <button
        type="button"
        onClick={() => { setView("planning"); setMenuOpen(false); }}
        className={`w-full flex items-center gap-4 px-5 py-3.5 rounded-2xl text-left font-medium text-[15px] transition-all ${
          view === "planning"
            ? "bg-white/15 text-white shadow-inner"
            : "text-indigo-100 hover:bg-white/10 hover:text-white"
        }`}
      >
        <Calendar size={22} /> Planning
      </button>
    </nav>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20 md:pb-0 flex">
      {/* Sidebar desktop — brand = première carte hero, puis nav */}
      <aside className="hidden md:flex md:w-72 md:flex-col md:fixed md:inset-y-0 z-20 bg-indigo-900/95 border-r border-white/5 shadow-2xl backdrop-blur-xl">
        {brandCard}
        {navContent}
      </aside>

      <div className="flex-1 md:ml-72 min-w-0">
        <header className="z-30 mx-4 md:mx-8 mt-4 md:mt-8 mb-6 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200/80 dark:border-white/10 shadow-xl shadow-gray-200/30 dark:shadow-none px-5 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4 min-w-0">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="md:hidden p-2.5 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 shrink-0"
              aria-label="Menu"
            >
              <Menu size={24} />
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-white tracking-tight truncate">
                {viewTitle}
              </h1>
              <p className="text-sm text-gray-500 dark:text-slate-400 truncate md:hidden mt-0.5">
                {ownerCompanyName ? `${ownerCompanyName} · ` : ""}{partnerName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <DarkModeToggle />
            <button
              onClick={onLogout}
              className="p-2 rounded-xl text-gray-400 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
            >
              <LogOut size={20} />
            </button>
          </div>
        </header>

        {/* Drawer mobile */}
        {menuOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/50 dark:bg-black/60 backdrop-blur-sm md:hidden"
              aria-hidden
              onClick={() => setMenuOpen(false)}
            />
            <div className="fixed inset-y-0 left-0 z-50 w-80 max-w-[90vw] bg-indigo-900/98 border-r border-white/5 shadow-2xl flex flex-col animate-in slide-in-from-left duration-200 md:hidden backdrop-blur-xl">
              <div className="relative mx-4 mt-4 mb-2 rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-xl shrink-0">
                <div className="aspect-[3/2] relative">
                  {ownerLogoUrl ? (
                    <img src={ownerLogoUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
                      <Briefcase className="w-12 h-12 text-white/30" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h2 className="text-base font-semibold text-white tracking-tight truncate">
                        {ownerCompanyName || "Menu"}
                      </h2>
                      <p className="text-xs text-white/80 truncate mt-0.5">Espace Partenaire</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setMenuOpen(false)}
                      className="p-2.5 rounded-xl text-white/90 hover:bg-white/15 shrink-0 transition-colors"
                      aria-label="Fermer"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>
              </div>
              {navContent}
            </div>
          </>
        )}

      {view === "planning" && (
        <div className="px-2 sm:px-4 md:px-8 pb-8 max-w-4xl mx-auto min-w-0 overflow-x-auto space-y-6">
          <div className="overflow-x-auto min-w-0">
            <PlanningCalendar
              jobs={jobs.filter((j) => j.date)}
              onJobSelect={(job) => {
                setEditingJobReadOnly(false);
                setEditingJob(job);
              }}
              readOnly={!canPartnerManageOwnSchedule}
            />
          </div>
          {/* Liste des chantiers en cours — ouverture éditable comme la liste dossiers */}
          <div className="rounded-2xl overflow-hidden bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border border-gray-200/60 dark:border-white/10 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.02)] dark:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.3)]">
            <div className="p-4 md:p-5 border-b border-gray-100 dark:border-white/5">
              <h3 className="text-[15px] font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Clock size={18} className="text-amber-500" />
                {t("admin.jobsInProgress")}
              </h3>
            </div>
            <div className="p-4 md:p-5">
              {inProgressJobs.length === 0 ? (
                <p className="text-[14px] text-gray-500 dark:text-slate-400">{t("admin.noJobsInProgress")}</p>
              ) : (
                <ul className="space-y-2">
                  {inProgressJobs.map((job) => (
                    <li key={job.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingJobReadOnly(false);
                          setEditingJob(job);
                        }}
                        className="w-full text-left flex flex-wrap items-center gap-x-3 gap-y-1 p-3 rounded-xl border border-amber-200/60 dark:border-amber-500/20 bg-amber-50/80 dark:bg-amber-500/10 hover:bg-amber-100/80 dark:hover:bg-amber-500/20 transition-colors cursor-pointer"
                      >
                        <span className="font-medium text-gray-900 dark:text-white truncate">{job.clientName}</span>
                        {job.date && (
                          <span className="text-[13px] text-gray-500 dark:text-slate-400">
                            {new Date(job.date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}
                            {job.time ? ` · ${job.time}` : ""}
                          </span>
                        )}
                        <ChevronRight size={16} className="ml-auto text-gray-400 dark:text-slate-500 shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {view === "list" && (
      <div className="px-4 md:px-8 pb-8 max-w-6xl mx-auto space-y-6">
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-4 px-5 rounded-2xl shadow-lg shadow-indigo-200/50 dark:shadow-indigo-900/20 flex items-center justify-center gap-3 font-semibold text-[15px] tracking-tight active:scale-[0.99] transition-all border border-indigo-500/20"
        >
          <Plus size={22} strokeWidth={2.5} /> Nouveau Dossier
        </button>
        <section className="space-y-4">
          {dossiersScreen === "hub" ? (
            <JobsCategoryHub
              jobs={jobs}
              accent="indigo"
              onSelect={handleSelectCategory}
            />
          ) : dossiersScreen === "types" && activeCategory ? (
            <JobsDossierTypeHub
              jobs={toScheduleJobsForTypes}
              category={activeCategory}
              accent="indigo"
              onSelect={handleSelectDossierType}
              onBack={goToDossiersHub}
            />
          ) : (
            <>
          <div className="flex flex-wrap items-center gap-3 px-1">
            <button
              type="button"
              onClick={
                activeCategory && categoryRequiresDossierType(activeCategory)
                  ? goToDossierTypes
                  : goToDossiersHub
              }
              className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
            >
              <ChevronLeft size={18} />
              {activeCategory && categoryRequiresDossierType(activeCategory)
                ? "Types"
                : "Catégories"}
            </button>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white tracking-tight flex-1 min-w-0">
              {activeCategory ? JOB_CATEGORY_LABELS[activeCategory] : "Vos dossiers"}
              {activeDossierType ? (
                <span className="text-gray-500 dark:text-slate-400 font-normal">
                  {" "}
                  · {DOSSIER_TYPE_FILTER_LABELS[activeDossierType]}
                </span>
              ) : null}
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-slate-400 tabular-nums">
                ({jobsToShow.length})
              </span>
            </h2>
          </div>
          <JobsFiltersBar
            filters={jobsFilters}
            stockRegions={stockRegions}
            layout="expanded"
            accent="indigo"
            showStatusPills={false}
            totalCount={jobs.length}
          />
          {jobsToShow.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-slate-400 bg-white dark:bg-slate-800/80 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm">
              <Briefcase size={44} className="mb-4 opacity-30" />
              <p className="text-[15px] font-medium">
                {jobs.length === 0 ? "Aucun dossier pour le moment" : "Aucun dossier ne correspond aux filtres"}
              </p>
              <p className="text-sm mt-1 opacity-80">
                {jobs.length === 0 ? "Utilisez le bouton ci‑dessus pour en créer un." : "Modifiez ou effacez les filtres."}
              </p>
            </div>
          ) : isGrouped ? (
            <div className="space-y-5">
              {jobsGrouped.map((group) => (
                <div key={group.status} className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 px-1 flex items-center gap-2">
                    {STATUS_LABELS[group.status]}
                    <span className="text-[10px] font-bold bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 rounded-full px-2 py-0.5">
                      {group.jobs.length}
                    </span>
                  </h3>
                  <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {group.jobs.map((job) => renderJobCard(job))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {jobsToShow.map((job) => renderJobCard(job))}
            </ul>
          )}
            </>
          )}
        </section>
      </div>
      )}
      </div>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} size="xl">
        <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-2xl bg-indigo-500/15 dark:bg-indigo-400/15 text-indigo-600 dark:text-indigo-400">
            <Briefcase size={22} strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Nouveau dossier</h3>
            <p className="text-sm md:text-[15px] text-gray-500 dark:text-slate-400 mt-0.5 md:mt-1">Renseignez les informations du client et du chantier</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5 rounded-2xl md:rounded-3xl bg-slate-50/80 dark:bg-slate-900/40 p-4 md:p-6">
          <section className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition-shadow overflow-visible">
            <div className="px-5 md:px-6 py-4 md:py-5 border-b border-slate-100 dark:border-white/10">
              <span className="font-semibold text-gray-900 dark:text-white">Contexte</span>
            </div>
            <div className="px-5 md:px-6 pb-5 md:pb-6 pt-4 md:pt-5 space-y-3 md:space-y-4">
                  {stockRegions.length > 0 ? (
                    <div>
                      <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 mb-1">Région (maison de stock)</label>
                      <div className="flex flex-wrap gap-2">
                        {stockRegions.map((r) => (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => setNewJob({ ...newJob, stockRegionId: r.id })}
                            className={`px-3 py-2 rounded-full border text-sm transition-colors ${
                              newJob.stockRegionId === r.id
                                ? "bg-indigo-600 text-white border-indigo-600"
                                : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200"
                            }`}
                          >
                            {r.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-slate-400 italic">Aucune région configurée.</p>
                  )}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition-shadow overflow-visible">
            <div className="px-5 md:px-6 py-4 md:py-5 border-b border-slate-100 dark:border-white/10">
              <span className="font-semibold text-gray-900 dark:text-white">Technique</span>
            </div>
            <div className="px-5 md:px-6 pb-5 md:pb-6 pt-4 md:pt-5 space-y-3 md:space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2">Type de dossier <span className="text-red-500">*</span></p>
                    <div
                      className="relative"
                      onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                          setIsDossierTypeMenuOpen(false);
                        }
                      }}
                    >
                      <button
                        type="button"
                        aria-haspopup="listbox"
                        aria-expanded={isDossierTypeMenuOpen}
                        onClick={() => setIsDossierTypeMenuOpen((open) => !open)}
                        className={`group flex w-full items-center justify-between gap-4 rounded-3xl border px-4 py-4 text-left shadow-lg shadow-indigo-100/50 outline-none transition-all duration-200 dark:shadow-black/20 ${
                          selectedDossierType
                            ? "border-indigo-300/80 bg-gradient-to-br from-indigo-50 via-white to-violet-50 dark:border-indigo-400/30 dark:from-slate-800 dark:via-slate-900 dark:to-indigo-950/30"
                            : "border-dashed border-slate-300 bg-white dark:border-white/15 dark:bg-slate-800/80"
                        } hover:-translate-y-0.5 hover:border-indigo-400/80 hover:shadow-xl focus:ring-4 focus:ring-indigo-500/15`}
                      >
                        <span className="flex min-w-0 items-center gap-3">
                          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-md shadow-indigo-500/25">
                            <Briefcase size={19} />
                          </span>
                          <span className="min-w-0">
                            <span className={`block truncate text-[15px] font-bold ${selectedDossierType ? "text-gray-950 dark:text-white" : "text-gray-500 dark:text-slate-400"}`}>
                              {selectedDossierType?.label ?? "Choisir le type de dossier"}
                            </span>
                            <span className="mt-0.5 block text-xs font-medium text-indigo-600/80 dark:text-indigo-300/80">
                              {selectedDossierType
                                ? usesRenovationQuestions(newJob.dossierType)
                                  ? "Questionnaire simplifié nom / prénom / notes"
                                  : "Questionnaire technique complet"
                                : "Sélection obligatoire avant création"}
                            </span>
                          </span>
                        </span>
                        <ChevronDown className={`h-5 w-5 shrink-0 text-indigo-500 transition-transform duration-200 dark:text-indigo-300 ${isDossierTypeMenuOpen ? "rotate-180" : ""}`} />
                      </button>
                      {isDossierTypeMenuOpen && (
                        <div
                          role="listbox"
                          className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-3xl border border-indigo-100 bg-white p-2 shadow-2xl shadow-slate-900/15 ring-1 ring-black/5 dark:border-white/10 dark:bg-slate-900 dark:shadow-black/40"
                        >
                          {DOSSIER_TYPE_OPTIONS.map((opt) => {
                            const isSelected = newJob.dossierType === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setNewJob({ ...newJob, dossierType: opt.value });
                                  setIsDossierTypeMenuOpen(false);
                                }}
                                className={`flex w-full items-center justify-between gap-3 rounded-2xl px-3 py-3 text-left transition-all ${
                                  isSelected
                                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-500/20"
                                    : "text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 dark:text-slate-200 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-200"
                                }`}
                              >
                                <span>
                                  <span className="block text-sm font-semibold">{opt.label}</span>
                                  <span className={`block text-xs ${isSelected ? "text-indigo-100" : "text-gray-400 dark:text-slate-500"}`}>
                                    {usesRenovationQuestions(opt.value)
                                      ? "Questions type rénovation"
                                      : "Questions techniques"}
                                  </span>
                                </span>
                                {isSelected && <CheckCircle size={18} className="shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  {usesTechnicalQuestions(newJob.dossierType) && (
                    <input type="text" placeholder="Surface (m²)" value={newJob.surface} onChange={(e) => setNewJob({ ...newJob, surface: e.target.value })} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                  )}
                  {showLedTechnicalFields(newJob.dossierType) && (
                    <>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: "visite technique", label: "Visite technique" },
                          { value: "pose", label: "Pose" },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setNewJob({ ...newJob, lightingType: opt.value })}
                            className={`px-3 py-2 rounded-full border text-sm transition-colors ${
                              newJob.lightingType === opt.value
                                ? "bg-indigo-600 text-white border-indigo-600"
                                : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      <input type="text" inputMode="numeric" placeholder="Nombre de LED estimé" value={newJob.estimatedLedCount} onChange={(e) => setNewJob({ ...newJob, estimatedLedCount: e.target.value })} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                    </>
                  )}
                  {usesTechnicalQuestions(newJob.dossierType) && (
                    <>
                      <input type="text" placeholder="Hauteur sous plafond (ex: 3m)" value={newJob.ceilingHeight} onChange={(e) => setNewJob({ ...newJob, ceilingHeight: e.target.value })} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                      {showDehumidifierTechnicalFields(newJob.dossierType) && (
                        <>
                          <input type="number" min={0} placeholder="Nombre de déshumidificateurs" value={newJob.dehumidifierCount} onChange={(e) => setNewJob({ ...newJob, dehumidifierCount: e.target.value })} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                              <input type="checkbox" checked={newJob.needsCounter} onChange={(e) => setNewJob({ ...newJob, needsCounter: e.target.checked })} />
                              Compteur
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                              <input type="checkbox" checked={newJob.needsGenerator} onChange={(e) => setNewJob({ ...newJob, needsGenerator: e.target.checked })} />
                              Générateur
                            </label>
                          </div>
                        </>
                      )}
                      {showTimerTechnicalFields(newJob.dossierType) && (
                        <input type="number" min={0} placeholder="Nombre de minuteries" value={newJob.timerCount} onChange={(e) => setNewJob({ ...newJob, timerCount: e.target.value })} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                      )}
                      {!isPacDossierType(newJob.dossierType) && (
                        <textarea placeholder="Infos d'accès, remarques..." value={newJob.notes} onChange={(e) => setNewJob({ ...newJob, notes: e.target.value })} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white min-h-[88px] resize-y focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                      )}
                    </>
                  )}
            </div>
          </section>
          <section className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            <div className="px-5 md:px-6 py-4 md:py-5 border-b border-slate-100 dark:border-white/10">
              <span className="font-semibold text-gray-900 dark:text-white">Infos Client</span>
            </div>
            <div className="px-5 md:px-6 pb-5 md:pb-6 pt-4 md:pt-5 space-y-3 md:space-y-4">
                  {usesRenovationQuestions(newJob.dossierType) ? (
                    <>
                      <input required type="text" placeholder="Quel est le nom du client ?" value={newJob.clientLastName} onChange={(e) => setNewJob({ ...newJob, clientLastName: e.target.value })} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                      <input required type="text" placeholder="Quel est le prénom du client ?" value={newJob.clientFirstName} onChange={(e) => setNewJob({ ...newJob, clientFirstName: e.target.value })} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                    </>
                  ) : (
                    <>
                      <input required={!isPacDossierType(newJob.dossierType)} type="text" placeholder="Nom du client" value={newJob.clientName} onChange={(e) => setNewJob({ ...newJob, clientName: e.target.value })} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                      {isPacDossierType(newJob.dossierType) && (
                        <>
                          <input type="text" placeholder="Quel est le nom du client ?" value={newJob.clientLastName} onChange={(e) => setNewJob({ ...newJob, clientLastName: e.target.value })} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                          <input type="text" placeholder="Quel est le prénom du client ?" value={newJob.clientFirstName} onChange={(e) => setNewJob({ ...newJob, clientFirstName: e.target.value })} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                        </>
                      )}
                    </>
                  )}
                  <input required type="tel" placeholder={usesRenovationQuestions(newJob.dossierType) || isPacDossierType(newJob.dossierType) ? "Quel est le téléphone du client ?" : "Téléphone"} value={newJob.clientPhone} onChange={(e) => setNewJob({ ...newJob, clientPhone: e.target.value })} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                  <AddressInput ref={addressRef} />
                  {(usesRenovationQuestions(newJob.dossierType) || isPacDossierType(newJob.dossierType)) && (
                    <textarea placeholder="Quelles notes souhaitez-vous ajouter ?" value={newJob.notes} onChange={(e) => setNewJob({ ...newJob, notes: e.target.value })} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white min-h-[88px] resize-y focus:ring-2 focus:ring-indigo-500/20 outline-none" />
                  )}
            </div>
          </section>
          {canPartnerManageOwnSchedule && (
            <section className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              <div className="px-5 md:px-6 py-4 md:py-5 border-b border-slate-100 dark:border-white/10">
                <span className="font-semibold text-gray-900 dark:text-white">Rendez-vous (optionnel)</span>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Si renseigné, le dossier est envoyé comme programmé.</p>
              </div>
              <div className="px-5 md:px-6 pb-5 md:pb-6 pt-4 md:pt-5 flex flex-wrap gap-3">
                <div className="min-w-[140px] flex-1">
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 mb-1">Date</label>
                  <input
                    type="date"
                    value={newJob.date}
                    onChange={(e) => setNewJob({ ...newJob, date: e.target.value, time: e.target.value ? newJob.time : "" })}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-800/70 text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div className="min-w-[120px] flex-1">
                  <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 mb-1">Heure</label>
                  <input
                    type="time"
                    value={newJob.time}
                    onChange={(e) => setNewJob({ ...newJob, time: e.target.value })}
                    disabled={!newJob.date}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-800/70 text-sm text-gray-900 dark:text-white disabled:opacity-50"
                  />
                </div>
              </div>
            </section>
          )}
          <button
            type="submit"
            disabled={submitting || !isPartnerNewJobComplete}
            className="w-full bg-gradient-to-r from-indigo-700 to-indigo-600 hover:from-indigo-800 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3.5 rounded-2xl font-semibold text-[15px] shadow-sm transition-all flex items-center justify-center gap-2 active:scale-[0.99]"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Envoi…
              </>
            ) : (
              "Envoyer le dossier"
            )}
          </button>
        </form>
      </Modal>

      <Modal isOpen={!!editingJob} onClose={() => { setEditingJob(null); setEditingJobReadOnly(false); }} size="xl">
        <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-2xl bg-amber-500/15 dark:bg-amber-400/15 text-amber-600 dark:text-amber-400">
            <Edit2 size={22} strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
              {editingJobReadOnly ? t("installer.viewOnlyInProgress") : "Modifier le dossier"}
            </h3>
            <p className="text-sm md:text-[15px] text-gray-500 dark:text-slate-400 mt-0.5 md:mt-1">
              {editingJobReadOnly ? "Consultation seule — ce chantier est en cours." : "Mettez à jour les informations du chantier"}
            </p>
          </div>
        </div>
        {editingJob && (
          <>
            <form onSubmit={(e) => { e.preventDefault(); if (!editingJobReadOnly) handleSaveEdit(e); }} className="space-y-5 md:space-y-6">
              <div className="space-y-4 md:space-y-5 rounded-2xl md:rounded-3xl bg-slate-50/80 dark:bg-slate-900/40 p-4 md:p-6">
                <section className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                  <div className="px-5 md:px-6 py-4 md:py-5 border-b border-slate-100 dark:border-white/10">
                    <span className="font-semibold text-gray-900 dark:text-white">Technique</span>
                  </div>
                  <div className="px-5 md:px-6 pb-5 md:pb-6 pt-4 md:pt-5 space-y-3 md:space-y-4">
                        <div className="flex flex-wrap gap-2">
                          {DOSSIER_TYPE_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              disabled={editingJobReadOnly}
                              onClick={() => setEditingJob({ ...editingJob, dossierType: opt.value })}
                              className={`px-3 py-2 rounded-full border text-sm transition-colors disabled:opacity-60 ${
                                editingJob.dossierType === opt.value
                                  ? "bg-indigo-600 text-white border-indigo-600"
                                  : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        <input type="text" placeholder="Surface (m²)" value={editingJob.surface ?? ""} onChange={(e) => setEditingJob({ ...editingJob, surface: e.target.value })} readOnly={editingJobReadOnly} disabled={editingJobReadOnly} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none disabled:opacity-80" />
                        {(editingJob.dossierType === "led_exterieur" || editingJob.dossierType === "led_interieur" || !editingJob.dossierType) && (
                          <>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { value: "visite technique", label: "Visite technique" },
                                { value: "pose", label: "Pose" },
                              ].map((opt) => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  disabled={editingJobReadOnly}
                                  onClick={() => setEditingJob({ ...editingJob, lightingType: opt.value })}
                                  className={`px-3 py-2 rounded-full border text-sm transition-colors disabled:opacity-60 ${
                                    editingJob.lightingType === opt.value
                                      ? "bg-indigo-600 text-white border-indigo-600"
                                      : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200"
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                            <input type="text" inputMode="numeric" placeholder="Nombre de LED estimé" value={editingJob.estimatedLedCount ?? ""} onChange={(e) => setEditingJob({ ...editingJob, estimatedLedCount: e.target.value })} readOnly={editingJobReadOnly} disabled={editingJobReadOnly} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none disabled:opacity-80" />
                          </>
                        )}
                        <input type="text" placeholder="Hauteur sous plafond (ex: 3m)" value={editingJob.ceilingHeight ?? ""} onChange={(e) => setEditingJob({ ...editingJob, ceilingHeight: e.target.value })} readOnly={editingJobReadOnly} disabled={editingJobReadOnly} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none disabled:opacity-80" />
                        <textarea placeholder="Infos d'accès, remarques..." value={editingJob.notes ?? ""} onChange={(e) => setEditingJob({ ...editingJob, notes: e.target.value })} readOnly={editingJobReadOnly} disabled={editingJobReadOnly} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white min-h-[88px] resize-y focus:ring-2 focus:ring-indigo-500/20 outline-none disabled:opacity-80" />
                        {scheduleEditableFor(editingJob) && !editingJobReadOnly ? (
                          <div className="space-y-3 rounded-xl border border-dashed border-slate-200 dark:border-white/10 bg-slate-50/60 dark:bg-white/[0.03] p-4">
                            <p className="text-sm font-medium text-gray-800 dark:text-slate-200">Rendez-vous</p>
                            <div className="flex flex-wrap gap-3">
                              <div className="min-w-[140px] flex-1">
                                <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 mb-1">Date</label>
                                <input
                                  type="date"
                                  value={editingJob.date ?? ""}
                                  onChange={(e) => {
                                    const d = e.target.value;
                                    setEditingJob({
                                      ...editingJob,
                                      date: d || undefined,
                                      time: d ? (editingJob.time ?? "") : undefined,
                                      status: d ? "scheduled" : "pending",
                                    });
                                  }}
                                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-white"
                                />
                              </div>
                              <div className="min-w-[120px] flex-1">
                                <label className="block text-[11px] font-medium text-gray-500 dark:text-slate-400 mb-1">Heure</label>
                                <input
                                  type="time"
                                  value={editingJob.time ?? ""}
                                  onChange={(e) => setEditingJob({ ...editingJob, time: e.target.value || undefined })}
                                  disabled={!editingJob.date}
                                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-white disabled:opacity-50"
                                />
                              </div>
                            </div>
                            {editingJob.date && (
                              <button
                                type="button"
                                className="text-sm text-amber-700 dark:text-amber-300 font-medium hover:underline"
                                onClick={() =>
                                  setEditingJob({
                                    ...editingJob,
                                    date: undefined,
                                    time: undefined,
                                    status: "pending",
                                  })
                                }
                              >
                                Retirer le rendez-vous
                              </button>
                            )}
                          </div>
                        ) : (
                          <section className="rounded-xl border border-dashed border-slate-200 dark:border-white/10 bg-slate-50/60 dark:bg-white/[0.03] p-3 text-sm text-slate-600 dark:text-slate-300">
                            <p>
                              Planning: {editingJob.date ? new Date(editingJob.date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "—"} {editingJob.time ? `· ${editingJob.time}` : ""}
                            </p>
                          </section>
                        )}
                        {editingJob.partnerInvoiceTotalCents != null && (
                          <p className="text-sm font-medium text-green-600 dark:text-green-400">
                            Facture partenaire : {(editingJob.partnerInvoiceTotalCents / 100).toFixed(2)} €
                          </p>
                        )}
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                  <div className="px-5 md:px-6 py-4 md:py-5 border-b border-slate-100 dark:border-white/10">
                    <span className="font-semibold text-gray-900 dark:text-white">Infos Client</span>
                  </div>
                  <div className="px-5 md:px-6 pb-5 md:pb-6 pt-4 md:pt-5 space-y-3 md:space-y-4">
                        <input required type="text" placeholder="Nom du client" value={editingJob.clientName} onChange={(e) => setEditingJob({ ...editingJob, clientName: e.target.value })} readOnly={editingJobReadOnly} disabled={editingJobReadOnly} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none disabled:opacity-80" />
                        <input required type="tel" placeholder="Téléphone" value={editingJob.clientPhone} onChange={(e) => setEditingJob({ ...editingJob, clientPhone: e.target.value })} readOnly={editingJobReadOnly} disabled={editingJobReadOnly} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none disabled:opacity-80" />
                        <input required type="text" placeholder="Adresse" value={editingJob.clientAddress} onChange={(e) => setEditingJob({ ...editingJob, clientAddress: e.target.value })} readOnly={editingJobReadOnly} disabled={editingJobReadOnly} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500/20 outline-none disabled:opacity-80" />
                        {stockRegions.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {stockRegions.map((r) => (
                              <button
                                key={r.id}
                                type="button"
                                disabled={editingJobReadOnly}
                                onClick={() => setEditingJob({ ...editingJob, stockRegionId: r.id })}
                                className={`px-3 py-2 rounded-full border text-sm transition-colors disabled:opacity-60 ${
                                  editingJob.stockRegionId === r.id
                                    ? "bg-indigo-600 text-white border-indigo-600"
                                    : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200"
                                }`}
                              >
                                {r.name}
                              </button>
                            ))}
                          </div>
                        )}
                  </div>
                </section>
              </div>
              <div className="flex gap-3 pt-1">
                {editingJobReadOnly ? (
                  <button
                    type="button"
                    onClick={() => { setEditingJob(null); setEditingJobReadOnly(false); }}
                    className="w-full py-3.5 rounded-2xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    Fermer
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setEditingJob(null)}
                      className="flex-1 py-3.5 rounded-2xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || !isPartnerEditDirty}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/25 active:scale-[0.99] transition-all"
                    >
                      {submitting ? <Loader2 size={18} className="animate-spin" /> : "Enregistrer"}
                    </button>
                  </>
                )}
              </div>
            </form>

            <div className="mt-5 space-y-5">
              <DossierNotes dossierId={editingJob.id} currentUser={currentUser} />
              <SignedQuoteUpload
                jobId={editingJob.id}
                existingPaths={editingJob.signedQuoteUrls}
                canDelete={!editingJobReadOnly}
                disabled={editingJobReadOnly}
                onChange={(nextPaths) => {
                  const updated = {
                    ...editingJob,
                    signedQuoteUrls: nextPaths.length ? nextPaths : undefined,
                  };
                  setEditingJob(updated);
                  setEditingJobInitial(updated);
                  onUpdateJob(updated);
                }}
              />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};
